import { useState } from 'react';
import { Popover } from '@material-ui/core';
import clsx from 'clsx';

import { PdvAngleUpIcon, PdvCheckIcon, PdvCloseIcon } from '../icons';
import { useStyles } from './styles';

interface FilterSelectProps {
  /** heading inside the panel, e.g. "Brands" */
  title: string;
  /** trigger text while nothing is picked, e.g. "All brands" */
  allLabel: string;
  /** 'all' when unfiltered */
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

/** A pill trigger opening a titled list panel; picking closes it, Clear resets. */
export const FilterSelect = ({
  title,
  allLabel,
  value,
  options,
  onChange
}: FilterSelectProps) => {
  const classes = useStyles();
  // state rather than a ref, so the panel re-renders once its anchor exists
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const picked = value !== 'all';

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={setAnchor}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={picked ? value : allLabel}
        className={clsx(classes.trigger, open && classes.open)}
        onClick={() => setOpen(o => !o)}>
        <span>{picked ? value : allLabel}</span>
        <PdvAngleUpIcon size={16} />
      </button>

      <Popover
        open={open && Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classes.panel }}
        PaperProps={{ role: 'dialog', 'aria-label': title }}>
        <div className={classes.head}>
          <span>{title}</span>
          {picked ? (
            <button
              type="button"
              className={classes.clear}
              onClick={() => choose('all')}>
              <PdvCloseIcon size={16} />
              Clear
            </button>
          ) : (
            <button
              type="button"
              aria-label="Close"
              className={classes.close}
              onClick={() => setOpen(false)}>
              <PdvCloseIcon />
            </button>
          )}
        </div>
        <div className={classes.list} role="listbox" aria-label={title}>
          {options.map(o => (
            <button
              key={o}
              type="button"
              role="option"
              aria-selected={value === o}
              className={clsx(classes.item, value === o && classes.itemOn)}
              onClick={() => choose(o)}>
              <span>{o}</span>
              {value === o && <PdvCheckIcon />}
            </button>
          ))}
          {!options.length && (
            <span className={classes.empty}>Nothing to filter by yet.</span>
          )}
        </div>
      </Popover>
    </>
  );
};
